(function () {
  "use strict";

  if (window.MarineMaxCoveoRequestPipeline) return;

  const REQUIRED_FUNCTION_DEPENDENCIES = Object.freeze([
    "applyCoveoCollectionBrandScopeToFacetRequests",
    "applyCoveoCollectionMembershipScopeToFacetRequests",
    "applyCoveoCollectionScopeToRequestCq",
    "applyAuthoritativeConditionSelectionToFacetRequests",
    "applyCoveoStoredRangeSelectionsToFacetRequests",
    "applyCoveoUrlMakeContextToRequestBody",
    "applyCoveoYearSelectionToFacetRequests",
    "applyCoveoYearSelectionToRequestCq",
    "applyPendingInitialCoveoUrlStateToFacetRequests",
    "applyStoredLocationContextWithoutRadiusToRequestBody",
    "facetIdUsesRangeServerDomainRefresh",
    "facetIdsMatchLength",
    "getActiveCoveoLocationContext",
    "getCoveoCollectionHandleFromViewUrl",
    "getCoveoFacetRequestFieldId",
    "getPendingInitialCoveoUrlState",
    "isCollectionInitialDistanceSuppressed",
    "normalizeCoveoCollectionHandleValue",
    "recordPendingInitialCoveoPrimaryRequestPatch",
    "rememberCoveoFacetRequestTemplates",
    "sanitizeCoveoFacetEmptyValuesForApi",
    "sanitizeCoveoFacetPriceDomainsForApiLimit",
    "stripCoveoFacetOnlyTargetSelfSelection",
    "stripCoveoManagedRangeSelectionsForDomain",
    "stripExplicitlyClearedCoveoManagedRanges",
    "stripInactiveCoveoDistanceRadiusFromRequestBody",
  ]);

  function createCoveoRequestPipeline(dependencies) {
    const deps = dependencies || {};
    const missingDependencies = REQUIRED_FUNCTION_DEPENDENCIES.filter(
      function (name) {
        return typeof deps[name] !== "function";
      },
    );
    const runtimeWindow = deps.windowObject || globalThis.window;
    const runtimeDocument = deps.documentObject || globalThis.document;
    const sortDefinitions = deps.sortDefinitions;
    const defaultResultsPerPage = Number(deps.defaultResultsPerPage);
    const collectionDistanceResultsPerPage = Number(
      deps.collectionDistanceResultsPerPage,
    );
    if (!runtimeWindow) missingDependencies.push("windowObject");
    if (!runtimeDocument) missingDependencies.push("documentObject");
    if (!sortDefinitions?.relevance) {
      missingDependencies.push("sortDefinitions.relevance");
    }
    if (!sortDefinitions?.["length_overall descending"]) {
      missingDependencies.push(
        'sortDefinitions["length_overall descending"]',
      );
    }
    if (!Number.isFinite(defaultResultsPerPage) || defaultResultsPerPage <= 0) {
      missingDependencies.push("defaultResultsPerPage");
    }
    if (
      !Number.isFinite(collectionDistanceResultsPerPage) ||
      collectionDistanceResultsPerPage <= 0
    ) {
      missingDependencies.push("collectionDistanceResultsPerPage");
    }
    if (missingDependencies.length) {
      throw new Error(
        `[Coveo Request Pipeline] Missing dependencies: ${missingDependencies.join(", ")}`,
      );
    }

    const {
      applyCoveoCollectionBrandScopeToFacetRequests,
      applyCoveoCollectionMembershipScopeToFacetRequests,
      applyCoveoCollectionScopeToRequestCq,
      applyAuthoritativeConditionSelectionToFacetRequests,
      applyCoveoStoredRangeSelectionsToFacetRequests,
      applyCoveoUrlMakeContextToRequestBody,
      applyCoveoYearSelectionToFacetRequests,
      applyCoveoYearSelectionToRequestCq,
      applyPendingInitialCoveoUrlStateToFacetRequests,
      applyStoredLocationContextWithoutRadiusToRequestBody,
      facetIdUsesRangeServerDomainRefresh,
      facetIdsMatchLength,
      getActiveCoveoLocationContext,
      getCoveoCollectionHandleFromViewUrl,
      getCoveoFacetRequestFieldId,
      getPendingInitialCoveoUrlState,
      isCollectionInitialDistanceSuppressed,
      normalizeCoveoCollectionHandleValue,
      recordPendingInitialCoveoPrimaryRequestPatch,
      rememberCoveoFacetRequestTemplates,
      sanitizeCoveoFacetEmptyValuesForApi,
      sanitizeCoveoFacetPriceDomainsForApiLimit,
      stripCoveoFacetOnlyTargetSelfSelection,
      stripCoveoManagedRangeSelectionsForDomain,
      stripExplicitlyClearedCoveoManagedRanges,
      stripInactiveCoveoDistanceRadiusFromRequestBody,
    } = deps;

    function classifyRequest(url, body) {
      const requestUrl = String(url || "");
      const isSearchEndpoint =
        /\/commerce\/v2\/search(?:[/?]|$)/i.test(requestUrl);
      const isListingEndpoint =
        /\/commerce\/v2\/listing(?:[/?]|$)/i.test(requestUrl);
      const isSearchOrListingEndpoint =
        isSearchEndpoint || isListingEndpoint;
      const isSearchOrListingRequest = !!(
        body?.query !== undefined ||
        body?.context?.view !== undefined ||
        isSearchOrListingEndpoint
      );
      const isProductListingRequest = !!(
        isListingEndpoint ||
        (!isSearchEndpoint &&
          body?.query === undefined &&
          /\/collections\//i.test(String(body?.context?.view?.url || "")))
      );
      return Object.freeze({
        isListingEndpoint,
        isProductListingRequest,
        isSearchEndpoint,
        isSearchOrListingEndpoint,
        isSearchOrListingRequest,
        requestUrl,
      });
    }

    function snapshotRequestHeaders(headersLike) {
      const headers = {};
      if (!headersLike) return headers;
      if (Array.isArray(headersLike)) {
        headersLike.forEach(function (entry) {
          if (Array.isArray(entry) && entry.length >= 2) {
            headers[String(entry[0])] = String(entry[1]);
          }
        });
        return headers;
      }
      if (typeof headersLike.forEach === "function") {
        headersLike.forEach(function (value, key) {
          headers[String(key)] = String(value);
        });
        return headers;
      }
      Object.keys(headersLike).forEach(function (key) {
        headers[key] = String(headersLike[key]);
      });
      return headers;
    }

    function recordRequestDebug(url, body, transport) {
      let debugEnabled = false;
      try {
        debugEnabled =
          new URL(runtimeWindow.location.href).searchParams.get(
            "coveo-debug-requests",
          ) === "1";
      } catch (_) { }
      if (!debugEnabled || !body || typeof body !== "object") return;
      const endpointMatch = String(url || "").match(
        /\/commerce\/v2\/(search|listing)(?:[/?]|$)/i,
      );
      if (!endpointMatch) return;

      const root = runtimeDocument.documentElement;
      if (!root) return;
      let records = [];
      try {
        records = JSON.parse(root.dataset.coveoCommerceRequests || "[]");
      } catch (_) { }
      if (!Array.isArray(records)) records = [];
      records.push({
        endpoint: endpointMatch[1].toLowerCase(),
        transport: String(transport || ""),
        query: String(body.query ?? body.q ?? ""),
        page: Number(body.page ?? 0),
        resultCount: Number(
          body.numberOfResults ?? body.pageSize ?? body.perPage ?? 0,
        ),
        facetCount: Array.isArray(body.facets) ? body.facets.length : 0,
        facetIds: Array.isArray(body.facets)
          ? body.facets
              .map(function (facet) {
                return getCoveoFacetRequestFieldId(facet);
              })
              .filter(Boolean)
          : [],
        facetDescriptors: Array.isArray(body.facets)
          ? body.facets.map(function (facet) {
              return {
                facetId: String(facet?.facetId || ""),
                field: String(facet?.field || ""),
                interval: String(facet?.interval || ""),
                numberOfValues: Number(facet?.numberOfValues || 0),
                type: String(facet?.type || ""),
              };
            })
          : [],
        sortCriteria: String(
          body.sort?.sortCriteria || body.sort?.by || "",
        ),
        sortField: String(
          body.sort?.fields?.[0]?.field || body.sort?.fields?.[0]?.name || "",
        ),
        sortDirection: String(
          body.sort?.fields?.[0]?.direction || body.sort?.fields?.[0]?.by || "",
        ),
        collectionMembershipCount: Array.isArray(body.facets)
          ? body.facets.find(function (facet) {
              return String(
                facet?.facetId || facet?.field || "",
              ).toLowerCase() === "permanentid";
            })?.values?.length || 0
          : 0,
        collectionScopeType:
          Array.isArray(body.facets) &&
          body.facets.some(function (facet) {
            return !!(
              String(facet?.facetId || facet?.field || "").toLowerCase() ===
                "permanentid" &&
              Array.isArray(facet?.values) &&
              facet.values.length
            );
          })
            ? "permanentid-facet"
            : /@(permanentid|ec_product_id)==/i.test(String(body.cq || ""))
              ? "canonical-cq"
              : /@ec_collection_handle==/i.test(String(body.cq || ""))
                ? "handle-cq"
                : "none",
      });
      records = records.slice(-20);
      root.dataset.coveoCommerceRequestCount = String(records.length);
      root.dataset.coveoCommerceRequests = JSON.stringify(records);
    }

    function patchRequestBody(url, body, initLike) {
      const isCommerceRequestBody = !!(
        body &&
        (runtimeWindow.CoveoLocationService?.isCoveoCommerceRequestBody
          ? runtimeWindow.CoveoLocationService.isCoveoCommerceRequestBody(body)
          : body.query !== undefined ||
            body.context !== undefined ||
            Object.prototype.hasOwnProperty.call(body, "cq") ||
            body.facets !== undefined ||
            body.page !== undefined)
      );
      if (!isCommerceRequestBody) return null;

      const isFacetValuesOnlyRequest = body._mmFacetValuesOnly === true;
      const isCollectionMembershipPage =
        body._mmCollectionMembershipPage === true;
      const facetValuesOnlyTargetId = String(
        body._mmFacetValuesOnlyTargetId || "",
      );
      const isFacetValuesOnlyManagedRangeRequest = !!(
        isFacetValuesOnlyRequest &&
        facetValuesOnlyTargetId &&
        facetIdUsesRangeServerDomainRefresh(facetValuesOnlyTargetId)
      );
      delete body._mmFacetValuesOnly;
      delete body._mmFacetValuesOnlyTargetId;
      delete body._mmCollectionMembershipPage;

      const request = classifyRequest(url, body);
      const requestCollectionHandle =
        normalizeCoveoCollectionHandleValue(
          runtimeDocument.getElementById("coveo-headless-collection")?.dataset
            ?.collectionHandle,
        ) || getCoveoCollectionHandleFromViewUrl(body.context?.view?.url);
      const activeSortValue = String(
        runtimeWindow.__coveoActiveSortValue?.() || "",
      ).trim();

      /*
       * Canonical membership pages clone a normalized baseline Product
       * Listing request. Reapplying live state here would narrow membership to
       * the visitor's current filters.
       */
      if (isCollectionMembershipPage) return body;

      const usesClientNearestSort = !!(
        activeSortValue === "nearest" &&
        runtimeWindow.__coveoClientOnlyDistanceFilter !== false &&
        request.isProductListingRequest
      );
      const usesClientGeo =
        runtimeWindow.__coveoClientOnlyDistanceFilter !== false;
      if (usesClientNearestSort) {
        // Product Listing controllers don't expose a server-side distance
        // sort, so keep their existing client-side nearest fallback on a
        // relevance-ordered result pool.
        body.sort = sortDefinitions.relevance;
      } else if (
        request.isSearchOrListingRequest &&
        !isFacetValuesOnlyRequest
      ) {
        const controllerSort = body.sort;
        const hasControllerSort = !!(
          controllerSort &&
          typeof controllerSort === "object" &&
          (controllerSort.sortCriteria ||
            controllerSort.by ||
            (Array.isArray(controllerSort.fields) &&
              controllerSort.fields.length > 0))
        );
        if (!hasControllerSort) {
          body.sort =
            runtimeWindow.__coveoActiveSortPayload?.() ||
            sortDefinitions.relevance;
        }
      }
      if (
        isFacetValuesOnlyManagedRangeRequest &&
        facetIdsMatchLength(facetValuesOnlyTargetId)
      ) {
        body.sort = sortDefinitions["length_overall descending"];
      }

      if (isFacetValuesOnlyRequest) {
        body.numberOfResults = 1;
        body.pageSize = 1;
        body.perPage = 1;
      } else {
        const isClientDistanceProductListing = !!(
          request.isProductListingRequest &&
          runtimeWindow.__coveoClientOnlyDistanceFilter !== false &&
          getActiveCoveoLocationContext()
        );
        const resultsPerPage = isClientDistanceProductListing
          ? collectionDistanceResultsPerPage
          : defaultResultsPerPage;
        body.numberOfResults = resultsPerPage;
        body.pageSize = resultsPerPage;
        body.perPage = resultsPerPage;
      }

      let appliedLocationContext = false;
      if (
        !usesClientGeo &&
        runtimeWindow.CoveoLocationService?.applyLocationContextToRequestBody
      ) {
        if (!isCollectionInitialDistanceSuppressed()) {
          appliedLocationContext =
            runtimeWindow.CoveoLocationService.applyLocationContextToRequestBody(
              body,
            ) === true;
        }
      }
      if (
        !appliedLocationContext &&
        activeSortValue === "nearest"
      ) {
        appliedLocationContext =
          applyStoredLocationContextWithoutRadiusToRequestBody(body, {
            ignoreSuppression: true,
          }) === true;
      }
      if (!appliedLocationContext) {
        stripInactiveCoveoDistanceRadiusFromRequestBody(body, {
          force: usesClientGeo && activeSortValue !== "nearest",
        });
      }

      if (
        body.context?.view?.url?.includes("shopifypreview.com") &&
        typeof runtimeWindow.__coveoCanonicalViewUrl === "string"
      ) {
        body.context.view = Object.assign({}, body.context.view, {
          url: runtimeWindow.__coveoCanonicalViewUrl,
        });
      }

      const pendingUrlState = getPendingInitialCoveoUrlState();
      if (pendingUrlState?.page) {
        body.page = Math.max(0, Number(pendingUrlState.page) - 1);
      }
      rememberCoveoFacetRequestTemplates(body.facets);
      const pendingUrlFacetResult = request.isSearchOrListingEndpoint
        ? applyPendingInitialCoveoUrlStateToFacetRequests(body, {
            synthesizeMissingManagedRanges: !request.isProductListingRequest,
          })
        : null;
      if (!isFacetValuesOnlyRequest && request.isSearchOrListingEndpoint) {
        recordPendingInitialCoveoPrimaryRequestPatch(
          pendingUrlFacetResult?.facetIds || [],
        );
      }
      applyCoveoYearSelectionToFacetRequests(body);
      applyCoveoStoredRangeSelectionsToFacetRequests(body);
      if (request.isSearchOrListingEndpoint) {
        applyAuthoritativeConditionSelectionToFacetRequests(body);
      }
      applyCoveoCollectionBrandScopeToFacetRequests(body);
      applyCoveoUrlMakeContextToRequestBody(body);
      const hasCollectionMembershipFacet = !request.isProductListingRequest
        ? applyCoveoCollectionMembershipScopeToFacetRequests(body)
        : false;
      applyCoveoCollectionScopeToRequestCq(body, {
        useCanonicalMembership: !request.isProductListingRequest,
        collectionHandle: requestCollectionHandle,
        skipCollectionScope: hasCollectionMembershipFacet,
      });
      stripExplicitlyClearedCoveoManagedRanges(body);

      if (isFacetValuesOnlyRequest && facetValuesOnlyTargetId) {
        if (isFacetValuesOnlyManagedRangeRequest) {
          stripCoveoManagedRangeSelectionsForDomain(
            body,
            facetValuesOnlyTargetId,
          );
        } else {
          stripCoveoFacetOnlyTargetSelfSelection(
            body,
            facetValuesOnlyTargetId,
          );
        }
      }
      sanitizeCoveoFacetPriceDomainsForApiLimit(body);
      sanitizeCoveoFacetEmptyValuesForApi(body, {
        dropEmptyFacets: !request.isProductListingRequest,
        preserveFacetId:
          isFacetValuesOnlyRequest &&
          facetValuesOnlyTargetId &&
          !isFacetValuesOnlyManagedRangeRequest
            ? facetValuesOnlyTargetId
            : "",
      });
      applyCoveoYearSelectionToRequestCq(body);

      const collectionRoot = runtimeDocument.getElementById(
        "coveo-headless-collection",
      );
      if (
        request.isListingEndpoint &&
        request.isProductListingRequest &&
        !isFacetValuesOnlyRequest &&
        Number(body.page ?? 0) === 0 &&
        collectionRoot &&
        requestCollectionHandle &&
        requestCollectionHandle ===
          normalizeCoveoCollectionHandleValue(
            collectionRoot.dataset?.collectionHandle,
          )
      ) {
        const existingTemplate =
          collectionRoot._coveoCollectionListingTemplateRequest;
        if (
          !existingTemplate ||
          existingTemplate.handle !== requestCollectionHandle ||
          existingTemplate.failed === true
        ) {
          const templateToken = Object.freeze({});
          try {
            collectionRoot._coveoCollectionListingTemplateRequest = {
              token: templateToken,
              handle: requestCollectionHandle,
              url: String(url || ""),
              init: initLike
                ? {
                    method: initLike.method,
                    credentials: initLike.credentials,
                    mode: initLike.mode,
                    headers: snapshotRequestHeaders(initLike.headers),
                  }
                : null,
              body: JSON.parse(JSON.stringify(body)),
              successful: false,
              failed: false,
            };
            Object.defineProperty(body, "__mmCollectionListingTemplateToken", {
              configurable: true,
              enumerable: false,
              value: templateToken,
            });
          } catch (_) { }
        }
      }

      const isCollectionScopePrefetchRequest = !!(
        request.isProductListingRequest &&
        runtimeWindow.__coveoCollectionScopePrefetchLoading
      );
      if (
        request.isSearchOrListingEndpoint &&
        !isFacetValuesOnlyRequest &&
        !isCollectionScopePrefetchRequest
      ) {
        try {
          runtimeWindow.__coveoLastPatchedCommerceRequest = {
            url,
            body: JSON.parse(JSON.stringify(body)),
          };
          runtimeWindow.__coveoLastCommerceRequest = {
            url,
            init: initLike
              ? {
                  method: initLike.method,
                  credentials: initLike.credentials,
                  mode: initLike.mode,
                  headers: snapshotRequestHeaders(initLike.headers),
                }
              : null,
            body: JSON.parse(JSON.stringify(body)),
          };
        } catch (_) { }
      }
      return body;
    }

    return Object.freeze({
      classifyRequest,
      patchRequestBody,
      recordRequestDebug,
      snapshotRequestHeaders,
    });
  }

  Object.defineProperty(window, "MarineMaxCoveoRequestPipeline", {
    configurable: false,
    enumerable: false,
    value: Object.freeze({ create: createCoveoRequestPipeline }),
    writable: false,
  });
})();
